import { useCallback, useEffect, useMemo, useState } from "react";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  EmptyState,
  Filters,
  Input,
  LoadingState,
  Pagination,
  Search,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createSupportApi } from "../api";
import { createUsersApi } from "../../users/api";
import { formatLabel, priorityTone, ticketStatusTone } from "../labels";
import type {
  Priority,
  SupportQueue,
  SupportTicketDto,
  SupportTicketStatus,
} from "../types";
import { CreateTicketDialog } from "../components/SupportDialogs";

const STATUSES: SupportTicketStatus[] = [
  "new",
  "open",
  "assigned",
  "pending_customer",
  "pending_internal_resolution",
  "escalated",
  "resolved",
  "closed",
  "reopened",
];

const QUEUES: SupportQueue[] = [
  "general",
  "booking",
  "payment",
  "creator",
  "business",
  "community",
  "trust_and_safety",
  "future_governed_type",
];

const PRIORITIES: Priority[] = ["low", "normal", "high", "urgent", "critical"];

const SORT_OPTIONS = [
  { value: "sla_due_at", label: "SLA due (soonest)" },
  { value: "-sla_due_at", label: "SLA due (latest)" },
  { value: "priority", label: "Priority" },
  { value: "status", label: "Status" },
  { value: "id", label: "ID" },
];

export function SupportTicketsDirectoryPage() {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Support" },
  ]);

  const client = useApiClient();
  const api = useMemo(() => createSupportApi(client), [client]);
  const usersApi = useMemo(() => createUsersApi(client), [client]);
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get("q") ?? "";
  const status = searchParams.get("status") ?? "";
  const queue = searchParams.get("queue") ?? "";
  const priority = searchParams.get("priority") ?? "";
  const ownerUserId = searchParams.get("owner_user_id") ?? "";
  const requesterUserId = searchParams.get("requester_user_id") ?? "";
  const marketId = searchParams.get("market_id") ?? "";
  const sort = searchParams.get("sort") ?? "sla_due_at";
  const cursor = searchParams.get("cursor") ?? undefined;

  const [rows, setRows] = useState<SupportTicketDto[]>([]);
  // Best-effort, deduplicated per page of results — the ticket list itself
  // only carries requester_user_id, so who a ticket is from is a second
  // lookup against the existing Users read, not a reason to block the list.
  const [requesterNames, setRequesterNames] = useState<Record<string, string | null>>({});
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState(q);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const page = cursorStack.length + 1;

  /** `silent` = the live-refresh path: no spinner, and a failed poll keeps the list. */
  const load = useCallback(async (options: { silent?: boolean } = {}) => {
    const silent = options.silent ?? false;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const list = await api.listTickets({
        ...(cursor ? { cursor } : {}),
        limit: 25,
        sort,
        ...(q ? { q } : {}),
        ...(status ? { status } : {}),
        ...(queue ? { queue } : {}),
        ...(priority ? { priority } : {}),
        ...(ownerUserId ? { owner_user_id: ownerUserId } : {}),
        ...(requesterUserId ? { requester_user_id: requesterUserId } : {}),
        ...(marketId ? { market_id: marketId } : {}),
      });
      setRows(list.tickets);
      setNextCursor(list.pagination.next_cursor ?? undefined);
    } catch (err) {
      if (!silent) {
        setError(apiErrorMessage(err));
        setRows([]);
        setNextCursor(undefined);
      }
    } finally {
      setLoading(false);
    }
  }, [
    api,
    cursor,
    sort,
    q,
    status,
    queue,
    priority,
    ownerUserId,
    requesterUserId,
    marketId,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  /** New tickets and customer replies land while the queue is open. */
  useLiveRefresh(() => {
    void load({ silent: true });
  }, { intervalMs: 10_000 });

  useEffect(() => {
    const unresolved = [
      ...new Set(rows.map((r) => r.requester_user_id)),
    ].filter((id) => !(id in requesterNames));
    if (unresolved.length === 0) return;
    let live = true;
    void Promise.all(
      unresolved.map((id) =>
        usersApi
          .getDetail(id)
          .then((r) => [id, r.user.display_name] as const)
          .catch(() => [id, null] as const),
      ),
    ).then((resolved) => {
      if (!live) return;
      setRequesterNames((prev) => ({ ...prev, ...Object.fromEntries(resolved) }));
    });
    return () => { live = false; };
    // requesterNames deliberately excluded — it's this effect's own output,
    // and depending on it would refetch everything it just resolved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, usersApi]);

  useEffect(() => {
    setSearchDraft(q);
  }, [q]);

  function updateParams(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  }

  function applySearch() {
    setCursorStack([]);
    updateParams({ q: searchDraft.trim() || undefined, cursor: undefined });
  }

  function goNext() {
    if (!nextCursor) return;
    setCursorStack((stack) => [...stack, cursor ?? ""]);
    updateParams({ cursor: nextCursor });
  }

  function goPrevious() {
    if (cursorStack.length === 0) return;
    const stack = [...cursorStack];
    const previous = stack.pop();
    setCursorStack(stack);
    updateParams({
      cursor: previous && previous.length > 0 ? previous : undefined,
    });
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Support"
        description="Customer Support ticket inbox. Tickets stay independent from Trust & Safety Moderation Cases — linked records are context only. External conversation is separate from Internal Notes."
        actions={
          <Flex gap={8}>
            <Button variant="secondary" onClick={() => setCreateOpen(true)}>
              Create Ticket
            </Button>
            <Button variant="secondary" onClick={() => void load()}>
              Refresh
            </Button>
          </Flex>
        }
      />

      <Filters>
        <div style={{ flex: "1 1 240px", minWidth: 200 }}>
          <Search
            value={searchDraft}
            onValueChange={setSearchDraft}
            placeholder="Search contact reason / public ID"
            onKeyDown={(event) => {
              if (event.key === "Enter") applySearch();
            }}
          />
        </div>
        <Select
          label="Status"
          value={status}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              status: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All statuses" },
            ...STATUSES.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Queue"
          value={queue}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              queue: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All queues" },
            ...QUEUES.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Priority"
          value={priority}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              priority: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All priorities" },
            ...PRIORITIES.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Input
          label="Owner User ID"
          value={ownerUserId}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              owner_user_id: event.target.value || undefined,
              cursor: undefined,
            });
          }}
        />
        <Input
          label="Requester User ID"
          value={requesterUserId}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              requester_user_id: event.target.value || undefined,
              cursor: undefined,
            });
          }}
        />
        <Input
          label="Market ID"
          value={marketId}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              market_id: event.target.value || undefined,
              cursor: undefined,
            });
          }}
        />
        <Select
          label="Sort"
          value={sort}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({ sort: event.target.value, cursor: undefined });
          }}
          options={SORT_OPTIONS}
        />
        <Button variant="primary" onClick={applySearch}>
          Apply search
        </Button>
      </Filters>

      {error ? (
        <Alert tone="danger" title="Unable to load Support Tickets">
          {error}
        </Alert>
      ) : null}

      {loading ? <LoadingState message="Loading Support Tickets…" /> : null}

      {!loading && !error && rows.length === 0 ? (
        <EmptyState
          reason={
            q || status || queue || priority || ownerUserId
              ? "filters"
              : "none"
          }
          title="No Support Tickets found"
          action={
            <Button
              variant="secondary"
              onClick={() => {
                setCursorStack([]);
                setSearchParams(new URLSearchParams());
              }}
            >
              Clear filters
            </Button>
          }
        />
      ) : null}

      {!loading && rows.length > 0 ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Showing {rows.length} ticket{rows.length === 1 ? "" : "s"}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) goNext();
                  if (nextPage < page) goPrevious();
                }}
              />
            }
          />
          <Table>
            <Thead>
              <Tr>
                <Th>Ticket</Th>
                <Th>Requester</Th>
                <Th>Status</Th>
                <Th>Queue</Th>
                <Th>Priority</Th>
                <Th>Owner</Th>
                <Th>SLA due</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  <Td>
                    <Text role="bodyStrong" as="div">
                      {row.public_id}
                    </Text>
                    <Text role="caption" color="muted" as="div">
                      {row.contact_reason ?? "—"}
                    </Text>
                  </Td>
                  <Td>
                    <Link to={`/app/users/${row.requester_user_id}`}>
                      {requesterNames[row.requester_user_id] ?? row.requester_user_id}
                    </Link>
                  </Td>
                  <Td>
                    <StatusChip tone={ticketStatusTone(row.status)}>
                      {formatLabel(row.status)}
                    </StatusChip>
                  </Td>
                  <Td>{formatLabel(row.queue)}</Td>
                  <Td>
                    <StatusChip tone={priorityTone(row.priority)}>
                      {formatLabel(row.priority)}
                    </StatusChip>
                  </Td>
                  <Td>{row.owner_user_id ?? "Unassigned"}</Td>
                  <Td>{row.sla_due_at ?? "—"}</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() =>
                        void navigate(`/app/support/tickets/${row.id}`)
                      }
                    >
                      Open
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Stack>
      ) : null}

      <CreateTicketDialog
        open={createOpen}
        busy={createBusy}
        error={createError}
        onClose={() => {
          setCreateOpen(false);
          setCreateError(null);
        }}
        onSubmit={async (input) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.createTicket(input);
            toast.push({
              title: "Ticket created",
              description: result.ticket.public_id,
              tone: "success",
            });
            setCreateOpen(false);
            void navigate(`/app/support/tickets/${result.ticket.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />
    </Stack>
  );
}
